import React, { useState } from 'react';
import { Calendar, Users, MapPin, Play } from 'lucide-react';
import PhotoModal from './PhotoModal';

// Composant pour afficher vidéo ou image de couverture
const CoverMedia = ({ event }) => {
  const [showVideo, setShowVideo] = useState(false);

  // Regex mise à jour pour inclure SHORTS
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=|\/shorts\/))([\w-]{11})/);
    return match ? match[1] : null;
  };

  const hasVideo = event.coverVideo;
  const youTubeId = getYouTubeId(event.coverVideo);

  // Si on a activé la vidéo (clic sur play)
  if (hasVideo && showVideo) {
    if (youTubeId) {
        // YOUTUBE (Embed avec autoplay car l'utilisateur a cliqué)
        return (
            <div className="h-64 overflow-hidden relative">
                <iframe
                src={`https://www.youtube.com/embed/${youTubeId}?autoplay=1`}
                title={event.clientName}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                ></iframe>
            </div>
        );
    } else {
        // VIDÉO LOCALE (Lecture au clic)
        return (
            <div className="h-64 overflow-hidden relative bg-black">
                <video
                    src={event.coverVideo}
                    className="w-full h-full object-cover"
                    controls
                    autoPlay
                    playsInline
                />
            </div>
        );
    }
  }

  // AFFICHAGE PAR DÉFAUT (Image ou Miniature Vidéo)
  return (
    <div 
        className="h-64 overflow-hidden relative group cursor-pointer" 
        onClick={(e) => {
           // Si c'est une vidéo, on lance le player, sinon on laisse le parent ouvrir la modal
           if (hasVideo) {
             e.stopPropagation();
             setShowVideo(true);
           }
        }}
    >
      {event.coverImage ? (
          <img
            src={event.coverImage}
            alt={event.clientName}
            className="w-full h-full object-cover transition-transform group-hover:scale-105"
          />
      ) : hasVideo && !youTubeId ? (
          // Vidéo locale sans image de cover : On affiche la vidéo PAUSEE (sert d'image)
          <video
            src={event.coverVideo}
            className="w-full h-full object-cover"
            // Pas d'autoplay, pas de loop, juste l'image fixe
            preload="metadata"
          />
      ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400">
             {hasVideo ? "Vidéo disponible" : "Pas d'image"}
          </div>
      )}

      {/* OVERLAY BOUTON PLAY (Toujours visible si vidéo) */}
      {hasVideo && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/30 transition-colors">
          <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
            <Play size={32} className="text-black ml-1" fill="currentColor" />
          </div>
        </div>
      )}

      {/* Indicateur vidéo en haut à droite */}
      {hasVideo && (
        <div className="absolute top-2 right-2 bg-black/70 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
          <Play size={12} fill="currentColor" />
          Vidéo
        </div>
      )}
    </div>
  );
};

const EventAlbums = ({ events }) => {
  const [selectedEvent, setSelectedEvent] = useState(null);

  const openModal = (event) => {
    setSelectedEvent(event);
  };

  const closeModal = () => {
    setSelectedEvent(null);
  };

  const getMediaCount = (event) => {
    if (event.media && event.media.length > 0) {
      const images = event.media.filter(m => m.type === 'image').length;
      const videos = event.media.filter(m => m.type === 'video').length;
      return { images, videos, total: event.media.length };
    }
    return { images: event.images?.length || 0, videos: 0, total: event.images?.length || 0 };
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {events.map((event) => {
          const mediaCount = getMediaCount(event);
          return (
            <div
              key={event.id}
              className="event-album-card bg-white rounded-lg shadow-sm overflow-hidden transition-all hover:shadow-lg hover:-translate-y-1"
            >
              <CoverMedia event={event} />

              <div className="p-6 cursor-pointer" onClick={() => openModal(event)}>
                <h3 className="text-2xl font-bold mb-3 text-[#C24D36]" style={{ fontFamily: 'var(--font-serif)' }}>
                  {event.clientName}
                </h3>
                
                <div className="space-y-2 text-sm text-[#3E332E] font-sans mb-4">
                  <div className="flex items-center">
                    <Calendar size={16} className="mr-2" />
                    <span>{event.date}</span>
                  </div>
                  <div className="flex items-center text-[#5A6C45] font-bold">
                    <Users size={16} className="mr-2" />
                    <span>{event.guestCount}</span>
                  </div>
                  {event.location && (
                    <div className="flex items-center">
                      <MapPin size={16} className="mr-2" />
                      <span>{event.location}</span>
                    </div>
                  )}
                </div>

                {event.description && (
                  <p className="text-[#3E332E] mb-4 text-sm line-clamp-2 font-sans">
                    {event.description}
                  </p>
                )}

                <div className="border-t pt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-700 font-sans">
                      {mediaCount.images > 0 && `📸 ${mediaCount.images} photo${mediaCount.images > 1 ? "s" : ""}`}
                      {mediaCount.images > 0 && mediaCount.videos > 0 && ' · '}
                      {mediaCount.videos > 0 && `🎬 ${mediaCount.videos} vidéo${mediaCount.videos > 1 ? 's' : ''}`}
                    </span>
                    <span className="text-[#C24D36] font-semibold hover:underline font-sans">
                      Voir l&apos;album →
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {selectedEvent && (
        <PhotoModal
          event={selectedEvent}
          onClose={closeModal}
        />
      )}
    </>
  );
};

export default EventAlbums;